import React, { useState } from "react";
import {
  postGarante,
  subirDocumentoCliente,
} from "../helpers/rutaClientes";
import {
  leerDocumentoClienteOCR,
  mapOcrAClienteCompra,
} from "../helpers/rutaOcrDocumento";
import ModalDocumentoCropper from "./ModalDocumentoCropper";

/* ----------------------------- UTILIDADES ----------------------------- */

const clean = (v) => String(v ?? "").trim();
const upper = (v) => clean(v).toUpperCase();
const upperTyping = (v) => String(v ?? "").toUpperCase();
const lower = (v) => clean(v).toLowerCase();

const fechaHoyArgentina = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Buenos_Aires",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const get = (type) => parts.find((p) => p.type === type)?.value || "";

  return `${get("year")}-${get("month")}-${get("day")}`;
};

const makeGaranteInicial = () => ({
  apellido1: "",
  apellido2: "",
  nombre: "",
  dni: "",
  fecing: fechaHoyArgentina(),

  telefono1: "+1",
  email: "",

  domicilio: "",
  direccion2: "",
  ciudad: "",
  estado: "FL",
  zipCode: "",
  pais: "USA",

  esComprador: false,
  esGarante: true,
  activo: true,
});

const unwrapClienteGuardado = (resp) => {
  return resp?.cliente || resp?.data?.cliente || resp?.data || resp || null;
};

const mapOcrDorsoAGaranteDireccion = (datos = {}) => {
  const comprador =
    datos?.comprador ||
    datos?.clienteCompra ||
    datos?.persona ||
    {};

  const direccion =
    datos?.direccion ||
    comprador?.direccion ||
    {};

  const patch = {};

  const domicilio =
    direccion.domicilio ||
    direccion.direccion ||
    direccion.addressLine1 ||
    direccion.address ||
    comprador.domicilio ||
    comprador.direccion ||
    datos.domicilio ||
    "";

  const direccion2 =
    direccion.direccion2 ||
    direccion.apartamento ||
    direccion.apto ||
    direccion.suite ||
    direccion.unidad ||
    "";

  const ciudad =
    direccion.ciudad ||
    direccion.localidad ||
    direccion.municipio ||
    "";

  const estado =
    direccion.estado ||
    direccion.provincia ||
    "";

  const zipCode =
    direccion.zipCode ||
    direccion.codigoPostal ||
    direccion.postalCode ||
    direccion.zip ||
    "";

  const pais =
    direccion.pais ||
    direccion.country ||
    "";

  if (domicilio) patch.domicilio = upper(domicilio);
  if (direccion2) patch.direccion2 = upper(direccion2);
  if (ciudad) patch.ciudad = upper(ciudad);
  if (estado) patch.estado = upper(estado);
  if (zipCode) patch.zipCode = upper(zipCode);
  if (pais) patch.pais = upper(pais);

  return patch;
};

/* ----------------------------- COMPONENTE ----------------------------- */

const ModalGaranteFinanciacion = ({
  show = false,
  onClose,
  onSaved,
}) => {
  const [garante, setGarante] = useState(() => makeGaranteInicial());
  const [saving, setSaving] = useState(false);

  const [frenteFile, setFrenteFile] = useState(null);
  const [frentePreview, setFrentePreview] = useState("");
  const [frenteDatos, setFrenteDatos] = useState(null);
  const [leyendoFrente, setLeyendoFrente] = useState(false);

  const [dorsoFile, setDorsoFile] = useState(null);
  const [dorsoPreview, setDorsoPreview] = useState("");
  const [dorsoDatos, setDorsoDatos] = useState(null);
  const [leyendoDorso, setLeyendoDorso] = useState(false);

  const [crop, setCrop] = useState({
    show: false,
    tipo: "frente",
    file: null,
  });

  if (!show) return null;

  const limpiarPreviews = () => {
    if (frentePreview) URL.revokeObjectURL(frentePreview);
    if (dorsoPreview) URL.revokeObjectURL(dorsoPreview);
  };

  const resetModal = () => {
    limpiarPreviews();

    setGarante(makeGaranteInicial());

    setFrenteFile(null);
    setFrentePreview("");
    setFrenteDatos(null);
    setLeyendoFrente(false);

    setDorsoFile(null);
    setDorsoPreview("");
    setDorsoDatos(null);
    setLeyendoDorso(false);

    setCrop({
      show: false,
      tipo: "frente",
      file: null,
    });
  };

  const cerrar = () => {
    if (saving) return;

    resetModal();

    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleGarante = (e) => {
    const { name, value } = e.target;

    const camposTextoLibre = [
      "domicilio",
      "direccion2",
      "ciudad",
      "estado",
      "zipCode",
      "pais",
    ];

    setGarante((prev) => ({
      ...prev,
      [name]:
        name === "email"
          ? lower(value)
          : camposTextoLibre.includes(name)
          ? upperTyping(value)
          : upperTyping(value),
    }));
  };

  const seleccionarDocumento = (tipo, e) => {
    const file = e.target.files?.[0] || null;

    if (!file) return;

    setCrop({
      show: true,
      tipo,
      file,
    });

    e.target.value = "";
  };

  const cancelarCrop = () => {
    setCrop({
      show: false,
      tipo: "frente",
      file: null,
    });
  };

  const leerFrenteConIA = async (fileParam = null) => {
    const fileALeer = fileParam || frenteFile;

    if (!fileALeer) {
      alert("Primero seleccioná una imagen del frente del documento.");
      return;
    }

    try {
      setLeyendoFrente(true);

      const resp = await leerDocumentoClienteOCR({
        file: fileALeer,
        tipoDocumento: "frente",
        destino: "clienteCompra",
      });

      const datos = resp?.datos || null;

      if (!datos) {
        alert("La IA no devolvió datos del frente.");
        return;
      }

      setFrenteDatos(datos);

      const patch = mapOcrAClienteCompra(datos);

      setGarante((prev) => ({
        ...prev,
        ...patch,

        esComprador: false,
        esGarante: true,
      }));
    } catch (err) {
      console.error("❌ Error OCR frente garante:", err?.response?.data || err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo leer el frente del documento."
      );
    } finally {
      setLeyendoFrente(false);
    }
  };

  const leerDorsoConIA = async (fileParam = null) => {
    const fileALeer = fileParam || dorsoFile;

    if (!fileALeer) {
      alert("Primero seleccioná una imagen del dorso del documento.");
      return;
    }

    try {
      setLeyendoDorso(true);

      const resp = await leerDocumentoClienteOCR({
        file: fileALeer,
        tipoDocumento: "dorso",
        destino: "clienteCompra",
      });

      const datos = resp?.datos || null;

      if (!datos) {
        alert("La IA no devolvió datos del dorso.");
        return;
      }

      setDorsoDatos(datos);

      const patch = mapOcrDorsoAGaranteDireccion(datos);

      setGarante((prev) => ({
        ...prev,

        // El dorso solo completa domicilio.
        domicilio: patch.domicilio || prev.domicilio,
        direccion2: patch.direccion2 || prev.direccion2,
        ciudad: patch.ciudad || prev.ciudad,
        estado: patch.estado || prev.estado,
        zipCode: patch.zipCode || prev.zipCode,
        pais: patch.pais || prev.pais,

        esComprador: false,
        esGarante: true,
      }));
    } catch (err) {
      console.error("❌ Error OCR dorso garante:", err?.response?.data || err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo leer el dorso del documento."
      );
    } finally {
      setLeyendoDorso(false);
    }
  };

  const guardarRecorte = async ({ file, previewUrl }) => {
    const tipo = crop.tipo;

    setCrop({
      show: false,
      tipo: "frente",
      file: null,
    });

    if (tipo === "frente") {
      if (frentePreview) URL.revokeObjectURL(frentePreview);

      setFrenteFile(file);
      setFrentePreview(previewUrl || "");
      setFrenteDatos(null);

      await leerFrenteConIA(file);
      return;
    }

    if (tipo === "dorso") {
      if (dorsoPreview) URL.revokeObjectURL(dorsoPreview);

      setDorsoFile(file);
      setDorsoPreview(previewUrl || "");
      setDorsoDatos(null);

      await leerDorsoConIA(file);
    }
  };

  const limpiarFrente = () => {
    if (frentePreview) URL.revokeObjectURL(frentePreview);

    setFrenteFile(null);
    setFrentePreview("");
    setFrenteDatos(null);
  };

  const limpiarDorso = () => {
    if (dorsoPreview) URL.revokeObjectURL(dorsoPreview);

    setDorsoFile(null);
    setDorsoPreview("");
    setDorsoDatos(null);
  };

  const validar = () => {
    if (!upper(garante.nombre)) {
      return "Completá Nombre del garante.";
    }

    if (!upper(garante.apellido1)) {
      return "Completá Apellido 1 del garante.";
    }

    if (!upper(garante.dni)) {
      return "Completá ID / Documento del garante.";
    }

    return "";
  };

  const guardarGarante = async () => {
    const msg = validar();

    if (msg) {
      alert(msg);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        apellido1: upper(garante.apellido1),
        apellido2: upper(garante.apellido2),
        nombre: upper(garante.nombre),
        dni: upper(garante.dni),

        fecing: garante.fecing || null,

        telefono1: upper(garante.telefono1),
        email: lower(garante.email),

        domicilio: upper(garante.domicilio),
        direccion2: upper(garante.direccion2),
        ciudad: upper(garante.ciudad),
        estado: upper(garante.estado),
        zipCode: upper(garante.zipCode),
        pais: upper(garante.pais) || "USA",

        esComprador: false,
        esGarante: true,
        activo: true,
      };

      const resp = await postGarante(payload);
      const creado = unwrapClienteGuardado(resp);

      if (!creado?._id) {
        throw new Error("No se pudo obtener el ID del garante creado.");
      }

      const erroresDocumentos = [];

      if (frenteFile) {
        try {
          await subirDocumentoCliente(creado._id, "frente", frenteFile);
        } catch (err) {
          console.error("❌ Error subiendo frente garante:", err);
          erroresDocumentos.push("frente");
        }
      }

      if (dorsoFile) {
        try {
          await subirDocumentoCliente(creado._id, "dorso", dorsoFile);
        } catch (err) {
          console.error("❌ Error subiendo dorso garante:", err);
          erroresDocumentos.push("dorso");
        }
      }

      if (typeof onSaved === "function") {
        onSaved({
          ...creado,
          esComprador: false,
          esGarante: true,
        });
      }

      if (erroresDocumentos.length > 0) {
        alert(
          `Garante guardado, pero hubo problemas al subir documento: ${erroresDocumentos.join(
            ", "
          )}.`
        );
      }

      resetModal();

      if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      console.error("❌ Error guardando garante:", err?.response?.data || err);

      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo guardar el garante."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {!crop.show && (
        <div className="vr-garante-overlay">
          <div className="vr-garante-modal">
          <div className="vr-garante-header">
            <div>
              <h3>Nuevo garante / acompañante</h3>
              <p>
                Cargá el documento con IA y guardalo como garante de financiación.
              </p>
            </div>

            <button
              type="button"
              className="vr-garante-close"
              onClick={cerrar}
              disabled={saving}
            >
              ×
            </button>
          </div>

          <div className="vr-garante-body">
            <div className="vr-garante-docs">
              <div className="vr-garante-doc-card">
                <div className="vr-garante-doc-title">
                  ID garante frente *
                </div>

                <label className="vr-garante-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => seleccionarDocumento("frente", e)}
                    disabled={saving}
                  />

                  {frentePreview ? (
                    <img
                      src={frentePreview}
                      alt="Frente documento garante"
                    />
                  ) : (
                    <div>
                      <div className="vr-garante-camera">📷</div>
                      <strong>Subir fotografía</strong>
                      <span>Nombre, apellido e ID</span>
                    </div>
                  )}
                </label>

                <div className="vr-garante-doc-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => leerFrenteConIA()}
                    disabled={saving || leyendoFrente || !frenteFile}
                  >
                    {leyendoFrente ? "Leyendo..." : "Leer identidad"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    onClick={limpiarFrente}
                    disabled={saving || (!frenteFile && !frentePreview)}
                  >
                    Limpiar
                  </button>
                </div>

                {frenteDatos && (
                  <div className="vr-garante-ok">
                    Frente leído. Revisá los datos antes de guardar.
                  </div>
                )}
              </div>

              <div className="vr-garante-doc-card">
                <div className="vr-garante-doc-title">
                  ID garante reverso
                </div>

                <label className="vr-garante-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => seleccionarDocumento("dorso", e)}
                    disabled={saving}
                  />

                  {dorsoPreview ? (
                    <img
                      src={dorsoPreview}
                      alt="Dorso documento garante"
                    />
                  ) : (
                    <div>
                      <div className="vr-garante-camera">📷</div>
                      <strong>Subir fotografía</strong>
                      <span>Dirección / domicilio</span>
                    </div>
                  )}
                </label>

                <div className="vr-garante-doc-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => leerDorsoConIA()}
                    disabled={saving || leyendoDorso || !dorsoFile}
                  >
                    {leyendoDorso ? "Leyendo..." : "Leer domicilio"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    onClick={limpiarDorso}
                    disabled={saving || (!dorsoFile && !dorsoPreview)}
                  >
                    Limpiar
                  </button>
                </div>

                {dorsoDatos && (
                  <div className="vr-garante-ok">
                    Dorso leído. Revisá domicilio, ciudad, estado y ZIP.
                  </div>
                )}
              </div>
            </div>

            <div className="vr-garante-form">
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label">Nombre *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="nombre"
                    value={garante.nombre}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Apellido 1 *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="apellido1"
                    value={garante.apellido1}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Apellido 2</label>
                  <input
                    type="text"
                    className="form-control"
                    name="apellido2"
                    value={garante.apellido2}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">ID / Documento *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="dni"
                    value={garante.dni}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Teléfono</label>
                  <input
                    type="text"
                    className="form-control"
                    name="telefono1"
                    value={garante.telefono1}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={garante.email}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-8">
                  <label className="form-label">Dirección</label>
                  <textarea
                    className="form-control"
                    name="domicilio"
                    value={garante.domicilio}
                    onChange={handleGarante}
                    disabled={saving}
                    rows="2"
                    placeholder="Dirección / calle / número / referencias"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Apt / Suite / Unit</label>
                  <input
                    type="text"
                    className="form-control"
                    name="direccion2"
                    value={garante.direccion2}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Ciudad</label>
                  <input
                    type="text"
                    className="form-control"
                    name="ciudad"
                    value={garante.ciudad}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Estado</label>
                  <input
                    type="text"
                    className="form-control"
                    name="estado"
                    value={garante.estado}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">ZIP Code</label>
                  <input
                    type="text"
                    className="form-control"
                    name="zipCode"
                    value={garante.zipCode}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">País</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pais"
                    value={garante.pais}
                    onChange={handleGarante}
                    disabled={saving}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="vr-garante-footer">
            <button
              type="button"
              className="btn btn-outline-light"
              onClick={cerrar}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="button"
              className="btn btn-success"
              onClick={guardarGarante}
              disabled={saving}
            >
              {saving ? "Guardando..." : "Guardar garante"}
            </button>
          </div>
        </div>
      </div>
    )}

    <ModalDocumentoCropper
        show={crop.show}
        file={crop.file}
        titulo={
          crop.tipo === "frente"
            ? "Editor frente documento garante"
            : "Editor reverso documento garante"
        }
        subtitulo={
          crop.tipo === "frente"
            ? "Ajustá el área de identidad. Al guardar se leerá con IA."
            : "Ajustá el área de domicilio. Al guardar se leerá con IA."
        }
        onCancel={cancelarCrop}
        onSave={guardarRecorte}
      />
    </>
  );
};

export default ModalGaranteFinanciacion;